import type { Sheet } from "@fortune-sheet/core";
import { Close, PrintOutlined, RestartAlt, SaveOutlined } from "@mui/icons-material";
import { Box, Chip, IconButton, Modal, Typography } from "@mui/material";
import { motion } from "framer-motion";
import type { DateTime } from "luxon";
import { type FC, type MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { SaveAsDefaultDialog } from "../../components/SaveAsDefault/SaveAsDefaultDialog";
import { DownloadFormatButton } from "../../components/UIToolkit/Clickable/DownloadFormatButton";
import { SystemFlatSwitch } from "../../components/UIToolkit/Clickable/SystemFlatSwitch";
import { InlineHint } from "../../components/UIToolkit/molecules/InlineHint";
import { SpreadsheetPreview, type SpreadsheetPreviewHandle } from "../../components/UIToolkit/Previews/SpreadsheetPreview";
import SystemButton from "../../components/UIToolkit/SystemButton/SystemButton";
import { useAnalytics } from "../../contexts/analytics/hooks";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useUserSettingsContext } from "../../contexts/Staffing/UserSettingsContext";
import { generatePrintoutPdf } from "../../downloads/downloadDayViewPdf";
import { fit530PreviewColumnWidths, form530ExportColumnWidths, form530PageSetup } from "../../downloads/form530/create530Excel";
import { DEFAULT_FORM_530_SETTINGS, type Form530Settings } from "../../downloads/form530/pages";
import { printPdfBlob, handleDownload as triggerBrowserDownload } from "../../downloads/utils";
import { useDepartmentCallInsForDay } from "../../hooks/useCallIns";
import { useHistoryDistribution } from "../../hooks/useHistoryDistribution";
import { useLogProductEvent } from "../../hooks/useLogProductEvent";
import { useSaveAsDefaultEnabled } from "../../hooks/useSaveAsDefaultEnabled";
import { INK, NEUTRAL } from "../../theme/tokens";
import { DEFAULT_COLUMN_WIDTH, excelBufferToFortuneSheet } from "../../utils/excelToFortuneSheet";
import type { SheetStyleOverlay } from "../../utils/fortuneSheetStyleOverlay";
import { Form530SettingsPanel } from "./Form530SettingsPanel";
import { type DownloadType, type PrintoutColumnType, type PrintoutSettings, usePrintout } from "./Header/usePrintout";
import { PrintoutSettingsPanel, type PrintoutSettingsSection } from "./PrintoutSettingsPanel";

type DownloadFormat = "excel" | "pdf";

type DailyPrintoutButtonProps = { date: DateTime; showActual: boolean; locations: string[]; availableLocations: string[] };

const DEFAULT_SETTINGS_WIDTH = 280;
/** Must match the resize handle's own `width` below — it sits between the two panels. */
const RESIZE_HANDLE_WIDTH = 8;
const MIN_PANEL_WIDTH = 200;

const SUMMARY_ENABLED_SECTIONS = new Set<PrintoutSettingsSection>(["grouping", "summaryOptions"]);
const SUMMARY_GROUPING_TYPES: PrintoutColumnType[] = ["shiftTime"];

export const DailyPrintoutButton: FC<DailyPrintoutButtonProps> = ({ showActual, date, locations, availableLocations }) => {
	const { t } = useTranslation();
	const saveAsDefaultEnabled = useSaveAsDefaultEnabled();
	const { downloadOptions, handleDayViewDownload, handle530Download, generatePreviewBuffer, defaultPrintoutSettings } = usePrintout({
		date,
		selectedLocations: locations,
		availableLocations,
		showActual,
	});
	const { track } = useAnalytics();
	const { updateSettings, isLoading: isUpdatingSettings } = useUserSettingsContext();
	const { showErrorSnackbar } = useHospitalData();
	const { mutate: logProductEvent } = useLogProductEvent();

	// Fire-and-forget product-usage logging for the daily-printout metric (downloads + prints).
	// The settings payload is opaque; failures must never block or surface to the user.
	const logPrintout = useCallback(
		(action: "download" | "print", eventParams: Record<string, unknown>) => {
			logProductEvent({ eventType: "printout", action, eventParams: { ...eventParams, reportDate: date.toISODate() } });
		},
		[logProductEvent, date],
	);
	const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
	const [selectedType, setSelectedType] = useState<DownloadType>("detailed");
	const [form530Settings, setForm530Settings] = useState<Form530Settings>(DEFAULT_FORM_530_SETTINGS);
	const form530SettingsRef = useRef(form530Settings);
	form530SettingsRef.current = form530Settings;
	const [previewSheets, setPreviewSheets] = useState<Sheet[]>([]);
	const [isLoadingPreview, setIsLoadingPreview] = useState<boolean>(false);
	/**
	 * Cell formatting the user applied in the preview toolbar, for the life of this modal only — nothing
	 * is persisted and "Save as default" does not carry it.
	 *
	 * Held BESIDE `previewSheets` rather than written into them: the sheets array must stay referentially
	 * stable or `SpreadsheetPreview` remounts the widget and the user loses their selection, scroll
	 * position and active tab on every format click.
	 */
	const [styleOverlay, setStyleOverlay] = useState<SheetStyleOverlay>({});
	// Cell addresses are only meaningful against the workbook they were read from, so any rebuild drops
	// them. Structural on purpose: every path that reloads the preview — a settings change, the AI
	// configurator, a column-width reset, the 530 panel resize, a date or location change behind the
	// modal — produces a new `previewSheets` identity and is covered here without having to remember.
	// A column DRAG deliberately is not: `handleColumnWidthsChange` passes `reloadPreview: false`, and a
	// width moves no cell addresses.
	//
	// Derived during render rather than in an effect — React's own "adjusting state when a prop changes"
	// pattern, the same one `SpreadsheetPreview` uses to track its sheets. An effect would leave the stale
	// overlay in force for the render that first shows the REBUILT workbook, which is one render of wrong
	// formatting on screen and one render in which a download would apply addresses from the old sheet.
	const [overlaySource, setOverlaySource] = useState<Sheet[] | null>(null);
	if (overlaySource !== previewSheets) {
		setOverlaySource(previewSheets);
		if (Object.keys(styleOverlay).length > 0) setStyleOverlay({});
	}
	const hasFormatting = Object.keys(styleOverlay).length > 0;
	/** Bumped to force the widget back to the unformatted workbook when the user clears the overlay. */
	const [formattingResetKey, setFormattingResetKey] = useState<number>(0);
	const [printoutSettings, setPrintoutSettings] = useState<PrintoutSettings>(defaultPrintoutSettings);
	const [isPrinting, setIsPrinting] = useState<boolean>(false);
	// The PDF download is only awaitable work because of the formatting rebuild: with formatting applied
	// it re-runs the whole workbook build before rendering. `isUpdatingSettings` covers the settings save
	// that precedes it and then goes false, so without this the button would come back to life for the
	// slowest part of the click — and a second press would start a second rebuild and a second file.
	const [isBuildingPdf, setIsBuildingPdf] = useState<boolean>(false);
	const [isDownloading530, setIsDownloading530] = useState<boolean>(false);
	const printoutSettingsRef = useRef(printoutSettings);
	printoutSettingsRef.current = printoutSettings;
	const { data: shiftTimeAnchors, isLoading: isLoadingAnchors } = useHistoryDistribution(printoutSettings.alignGroupTimesToPopular);
	// `enabled` reacts to the live in-panel toggle state, so flipping the switch fetches immediately —
	// no staleness window — and gating on `selectedType === "detailed"` avoids a wasted fetch while a
	// non-detailed printout type is selected (call-outs are a detailed-only section).
	const { callIns: callOutShifts, isLoading: isLoadingCallOuts } = useDepartmentCallInsForDay({
		date,
		enabled: printoutSettings.showCallOutsSheet && selectedType === "detailed",
	});

	// Block downloads while anchors are still loading, so a download can't fire with the option on
	// but no anchors yet (which would silently produce per-row grouping). Same guard for call-outs.
	const disableDownload =
		isLoadingPreview ||
		isUpdatingSettings ||
		isBuildingPdf ||
		(printoutSettings.alignGroupTimesToPopular && isLoadingAnchors) ||
		(printoutSettings.showCallOutsSheet && isLoadingCallOuts);
	// The 530 is a fixed-layout government form, so it gets its own (much smaller) settings panel and
	// none of the day view's layout options — but the same preview and the same download controls.
	const is530 = selectedType === "530";

	useEffect(() => setPrintoutSettings(defaultPrintoutSettings), [defaultPrintoutSettings]);

	const settingsToPayload = useMemo(
		() => ({
			printGaps: printoutSettings.printGaps,
			showRoleGroupStats: printoutSettings.showRoleGroupStats,
			dailyPrintoutGrouping: printoutSettings.dailyPrintoutGrouping,
			divideGroupsByPage: printoutSettings.divideGroupsByPage,
			separateSheetsBy: printoutSettings.separateSheetsBy,
			customSheetTabs: printoutSettings.customSheetTabs,
			showAdmins: printoutSettings.showAdmins,
			showLocationCensus: printoutSettings.showLocationCensus,
			showPtoSheet: printoutSettings.showPtoSheet,
			showCallOutsSheet: printoutSettings.showCallOutsSheet,
			includeTimeOffReason: printoutSettings.includeTimeOffReason,
			printoutSortBy: printoutSettings.sortBy,
			printoutColumns: printoutSettings.columns,
			staffPerRow: printoutSettings.staffPerRow,
			rowGroupingBy: printoutSettings.rowGroupingBy,
			columnGroupingBy: printoutSettings.columnGroupingBy,
			spaceBetweenGroups: printoutSettings.spaceBetweenGroups,
			printoutOrientation: printoutSettings.orientation,
			printoutFitToPage: printoutSettings.fitToPage,
			printoutFitToWidth: printoutSettings.fitToWidth,
			printoutFitToHeight: printoutSettings.fitToHeight,
			hideHppdNursingHours: printoutSettings.hideHppdNursingHours,
			printoutColumnWidths: printoutSettings.printoutColumnWidths,
			flagNotOnboarded: printoutSettings.flagNotOnboarded,
			flagAgency: printoutSettings.flagAgency,
			mergeSimilarShiftTimes: printoutSettings.mergeSimilarShiftTimes,
			alignGroupTimesToPopular: printoutSettings.alignGroupTimesToPopular,
			customTimeGroups: printoutSettings.customTimeGroups,
			appendRoleToName: printoutSettings.appendRoleToName,
			customColumnNames: printoutSettings.customColumnNames,
			showDhppd: printoutSettings.showDhppd,
			showNhppd: printoutSettings.showNhppd,
			showActualStaffColumn: printoutSettings.showActualStaffColumn,
			showActualHoursColumn: printoutSettings.showActualHoursColumn,
			hppdIncludeTrainees: printoutSettings.hppdIncludeTrainees,
		}),
		[printoutSettings],
	);

	const autoSaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const hasChanged = useRef<boolean>(false);
	const clearTimeoutIfNeeded = useCallback(() => {
		if (autoSaveTimeoutRef.current) {
			clearTimeout(autoSaveTimeoutRef.current);
			autoSaveTimeoutRef.current = null;
		}
	}, []);

	useEffect(() => {
		/*This useEffect handles auto updating changed settings.*/
		if (!hasChanged.current) return;
		clearTimeoutIfNeeded();

		autoSaveTimeoutRef.current = setTimeout(() => {
			hasChanged.current = false;
			updateSettings(settingsToPayload).catch(() => {});
		}, 5_000);

		return () => {
			clearTimeoutIfNeeded();
		};
	}, [settingsToPayload, updateSettings, clearTimeoutIfNeeded]);

	const openModal = (event: MouseEvent<HTMLElement>): void => {
		event.stopPropagation();
		setIsModalOpen(true);
	};
	const closeModal = (): void => {
		setIsModalOpen(false);
		setPreviewSheets([]);
	};

	const [settingsWidth, setSettingsWidth] = useState<number>(DEFAULT_SETTINGS_WIDTH);
	const containerRef = useRef<HTMLDivElement>(null);
	// Read through a ref so `loadPreview` doesn't have to be rebuilt (and re-fire) on every drag frame.
	const settingsWidthRef = useRef(settingsWidth);
	settingsWidthRef.current = settingsWidth;

	/** The width the preview panel actually gets: the flex row minus the settings panel and the handle. */
	const previewWidthPx = useCallback((): number => (containerRef.current?.clientWidth ?? 0) - settingsWidthRef.current - RESIZE_HANDLE_WIDTH, []);

	const previewLoadId = useRef(0);
	const loadPreview = useCallback(async (): Promise<void> => {
		/**The load preview will only be called explicitly
		 * on mount or on setting update if the caller wants to.*/
		previewLoadId.current += 1;
		const thisLoadId = previewLoadId.current;
		setIsLoadingPreview(true);
		try {
			// The hook's cache isn't cleared when `enabled` turns false (see `useGetLimitedCallOffs`'s
			// always-disabled `combinedQuery`), so `callOutShifts` can still hold a previously-fetched,
			// non-empty array after the user flips the toggle off in this same page session. Re-check the
			// toggle here (via the ref, for the same stale-closure reason `printoutSettingsRef.current` is
			// used elsewhere in this callback) instead of trusting the raw hook output.
			const buffer = await generatePreviewBuffer(
				selectedType,
				printoutSettingsRef.current,
				printoutSettingsRef.current.showCallOutsSheet ? callOutShifts : [],
				shiftTimeAnchors,
				form530SettingsRef.current,
			);
			if (thisLoadId !== previewLoadId.current) return; // Stale call — a newer load superseded this one
			if (buffer) {
				// `applyWrapText`: both workbooks size every row to the lines its cells wrapped to — the day
				// view in its wrap pass, the 530 in its own fixed layout — so the preview has the vertical
				// space to show them, which is the converter's precondition for carrying the wrap mode across.
				// The 530's columns are additionally fixed to fill a printed page, so they get scaled into
				// whatever width the panel currently has rather than hiding the form behind a horizontal
				// scrollbar.
				const is530Preview = selectedType === "530";
				const sheets = await excelBufferToFortuneSheet(buffer, is530Preview ? fit530PreviewColumnWidths(previewWidthPx()) : undefined, {
					applyWrapText: true,
				});
				if (thisLoadId !== previewLoadId.current) return;
				setPreviewSheets(sheets);
			} else {
				// No workbook for this day. Drop whatever the last one produced rather than leaving it on
				// screen — a stale preview reads as this day's data, and every export builds from it.
				setPreviewSheets([]);
			}
		} catch {
			if (thisLoadId === previewLoadId.current) setPreviewSheets([]);
		} finally {
			if (thisLoadId === previewLoadId.current) setIsLoadingPreview(false);
		}
	}, [generatePreviewBuffer, selectedType, shiftTimeAnchors, callOutShifts, previewWidthPx]);

	const loadPreviewRef = useRef(loadPreview);
	loadPreviewRef.current = loadPreview;

	useEffect(() => {
		if (isModalOpen) loadPreview();
	}, [isModalOpen, loadPreview]);

	/**
	 * The sheets the PDF renders, which is the whole of what formatting changes about this path.
	 *
	 * Unformatted, these are the preview's own sheets — the cheapest thing that is also exactly right.
	 * Formatted, they are a REBUILD: `createDailySchedule` run again with the overlay baked in, then
	 * converted. The difference matters for one attribute only, and it is why the rebuild exists.
	 * Painting the preview's sheets directly would set a cell's style on sheets whose `rowlen` was
	 * computed from the UNFORMATTED workbook, so a font the user enlarged would get no more row than
	 * `effectiveRowHeightsPt` can give it (29.85pt for 24pt type) while the xlsx, whose `applyWrapText`
	 * pass runs after the overlay, gives it 33pt. Rebuilding puts both outputs downstream of the same
	 * row-sizing pass — and downstream of the SAME applier — so the page grid agrees by construction
	 * rather than by two implementations staying in step.
	 *
	 * The 530 never rebuilds: it is a fixed state form with its own workbook and its own preview, and
	 * formatting is withheld from it upstream (`previewFormatting` is `undefined` when `is530`).
	 */
	const sheetsForPdf = async (): Promise<Sheet[]> => {
		// The 530's preview columns are scaled down to fit the settings panel, which is a screen concern
		// only. Restore the form's real widths before rendering, or the printed page would come out as
		// wide as the panel the user happened to leave open.
		if (is530) return previewSheets.map((sheet) => ({ ...sheet, config: { ...sheet.config, columnlen: form530ExportColumnWidths() } }));
		if (!hasFormatting) return previewSheets;

		const buffer = await generatePreviewBuffer(
			selectedType,
			printoutSettingsRef.current,
			printoutSettingsRef.current.showCallOutsSheet ? callOutShifts : [],
			shiftTimeAnchors,
			form530SettingsRef.current,
			styleOverlay,
		);
		// A rebuild that produced nothing must not silently fall back to the unformatted preview sheets:
		// that PDF would disagree with the xlsx the same click's sibling button hands over, which is the
		// one thing this path exists to prevent. Let the caller's snackbar report it instead.
		if (!buffer) throw new Error("printout workbook rebuild produced no file");
		return await excelBufferToFortuneSheet(buffer, undefined, { applyWrapText: true });
	};

	/**
	 * The PDF for whatever tab is open, rendered from the workbook the preview is showing.
	 *
	 * Every tab — the day view and the 530 alike — goes through here, so a PDF is never a second
	 * hand-built rendering of the same data: it is the previewed workbook, and what a user approves on
	 * screen is what downloads and what prints.
	 */
	const buildPrintoutPdf = async (): Promise<Blob | null> => {
		if (previewSheets.length === 0) return null;
		const sheets = await sheetsForPdf();
		// The 530 renders under the FORM's own page setup, not the day view's — the same values
		// `create530Excel` built these worksheets against. A page setup resolved twice is a page setup
		// that can disagree, and what the two would disagree about is where the page ends.
		if (is530) {
			const setup = form530PageSetup(printoutSettings.fitToPage);
			return generatePrintoutPdf(sheets, setup.orientation, setup.fitToPage, setup.fitToWidth, setup.fitToHeight);
		}
		return generatePrintoutPdf(sheets, printoutSettings.orientation, printoutSettings.fitToPage, printoutSettings.fitToWidth, printoutSettings.fitToHeight);
	};

	/**
	 * Hands the browser the printout PDF as a file. Guarded exactly like `printBuiltPdf` below.
	 *
	 * The shared Excel-to-PDF path throws rather than guessing when it meets a workbook it cannot
	 * render faithfully (an unknown vertical alignment, an unparseable print-titles range). That is the
	 * right call inside the renderer, but on a click handler an unhandled throw is a silent failure from
	 * the user's side — no snackbar, no tracking, just a download that never arrives. A null blob is
	 * thrown for the same reason `handle530Download` throws on one: nothing downloading looks exactly
	 * like a browser that blocked the file.
	 *
	 * Returns whether a file actually reached the browser, so the caller can keep the dialog open to
	 * retry from.
	 */
	const downloadAsPdf = async (): Promise<boolean> => {
		setIsBuildingPdf(true);
		try {
			const blob = await buildPrintoutPdf();
			if (!blob) throw new Error("printout PDF rendering produced no file");
			triggerBrowserDownload(blob, `daily_schedule_${date.toFormat("yyyy-MM-dd")}.pdf`, "application/pdf");
			return true;
		} catch (error) {
			showErrorSnackbar(t("download.pdf.failed"), {}, error);
			return false;
		} finally {
			setIsBuildingPdf(false);
		}
	};

	/** Hands the browser the printout PDF. Shared by both tabs' print buttons — only the logging differs. */
	const printBuiltPdf = async (): Promise<void> => {
		setIsPrinting(true);
		try {
			const blob = await buildPrintoutPdf();
			if (!blob) throw new Error("printout PDF rendering produced no file");
			// Awaited so a failure that only surfaces once the iframe loads (a missing contentWindow, or
			// `print()` itself throwing) is actually caught here instead of being reported as a success.
			await printPdfBlob(blob);
			// Inside the `try`, so a print that never happened leaves the dialog — and the preview and
			// settings behind it — standing for the retry.
			closeModal();
		} catch (error) {
			showErrorSnackbar(t("download.print.failed"), {}, error);
		} finally {
			setIsPrinting(false);
		}
	};

	const runDownload = async (format: DownloadFormat): Promise<void> => {
		clearTimeoutIfNeeded();
		hasChanged.current = false;
		logPrintout("download", { format, downloadType: selectedType, ...settingsToPayload });
		if (hasFormatting) track("printout_formatting_exported", { format, download_type: selectedType });
		try {
			await updateSettings(settingsToPayload);
		} catch {
			// Settings save failed — proceed with download anyway
		}

		if (format === "pdf") {
			// Only on success, as in `run530Download`: a failed render has already surfaced its snackbar,
			// and closing here would take the preview and settings down with it — leaving nothing to retry
			// from. The xlsx branch below needs no equivalent guard: `handleDayViewDownload` owns its own
			// try/catch and snackbar, and resolves either way.
			if (await downloadAsPdf()) closeModal();
			return;
		}

		// Same toggle re-check as `loadPreview` — the hook's cache can still hold a previously-fetched
		// array after the toggle is switched off, so it must not be used unconditionally here.
		const resolvedCallOutShifts = printoutSettings.showCallOutsSheet ? callOutShifts : [];
		// All three day-view types get the formatting: they share one preview and one workbook builder, so
		// excluding any would leave a toolbar that visibly does something for a printout whose download
		// ignores it.
		switch (selectedType) {
			case "detailed":
				await handleDayViewDownload(false, resolvedCallOutShifts, false, printoutSettings, shiftTimeAnchors, styleOverlay);
				break;
			case "summary":
				await handleDayViewDownload(true, resolvedCallOutShifts, false, printoutSettings, shiftTimeAnchors, styleOverlay);
				break;
			case "spreadsheet":
				await handleDayViewDownload(false, resolvedCallOutShifts, true, printoutSettings, shiftTimeAnchors, styleOverlay);
				break;
		}
		closeModal();
	};

	const run530Download = async (format: "xlsx" | "pdf"): Promise<void> => {
		logPrintout("download", { format, downloadType: "530", ...form530Settings });
		// Tracked separately from `isLoadingPreview`: building the workbook is its own in-flight state, and
		// without it a second click starts a second download of the same form.
		setIsDownloading530(true);
		try {
			// Only on success: a failed export has already surfaced its snackbar, and closing here would
			// take the preview and settings down with it — leaving nothing to retry from.
			if (await handle530Download(form530Settings, format, printoutSettings, buildPrintoutPdf)) closeModal();
		} finally {
			setIsDownloading530(false);
		}
	};

	const run530Print = async (): Promise<void> => {
		logPrintout("print", { downloadType: "530", ...form530Settings });
		await printBuiltPdf();
	};

	// PDF-only: printing hands the browser the same page-fit layout the PDF download builds, which
	// makes no sense for the raw Excel grid.
	const runPrint = async (): Promise<void> => {
		clearTimeoutIfNeeded();
		hasChanged.current = false;
		logPrintout("print", { downloadType: selectedType, ...settingsToPayload });
		if (hasFormatting) track("printout_formatting_exported", { format: "print", download_type: selectedType });
		try {
			await updateSettings(settingsToPayload);
		} catch {
			// Settings save failed — proceed with print anyway
		}

		await printBuiltPdf();
	};

	// Session-only (see `Form530SettingsPanel`), so there is no settings payload to autosave — just the
	// ref the preview loader reads, kept in step with state the same way `updateSetting` does.
	const updateForm530Setting = useCallback(<K extends keyof Form530Settings>(key: K, value: Form530Settings[K]) => {
		const updated = { ...form530SettingsRef.current, [key]: value };
		form530SettingsRef.current = updated;
		setForm530Settings(updated);
		loadPreviewRef.current();
	}, []);

	/**
	 * Applies a whole settings object in one go (the AI configurator changes many keys at once).
	 *
	 * Deliberately not N× `updateSetting`: that would rebuild the preview workbook once per key. One
	 * state write, one preview load — and marking `hasChanged` hands the result to the same 5s autosave
	 * effect every manual change already goes through, so nothing else needs to know about it.
	 */
	const applySettings = useCallback((next: PrintoutSettings) => {
		hasChanged.current = true;
		printoutSettingsRef.current = next;
		setPrintoutSettings(next);
		loadPreviewRef.current();
	}, []);

	const updateSetting = useCallback(<K extends keyof PrintoutSettings>(key: K, value: PrintoutSettings[K], reloadPreview = true) => {
		hasChanged.current = true;
		const updated = { ...printoutSettingsRef.current, [key]: value };
		printoutSettingsRef.current = updated;
		setPrintoutSettings(updated);
		if (reloadPreview) loadPreviewRef.current();
	}, []);

	const handleStyleOverlayChange = useCallback(
		(overlay: SheetStyleOverlay) => {
			// Only the transition out of "nothing formatted": one event per session, so the metric answers
			// "does anyone use this" rather than counting toolbar clicks.
			if (Object.keys(styleOverlay).length === 0 && Object.keys(overlay).length > 0) {
				track("printout_formatting_applied", { download_type: selectedType });
			}
			setStyleOverlay(overlay);
		},
		[styleOverlay, selectedType, track],
	);

	const clearFormatting = useCallback(() => {
		setStyleOverlay({});
		// The overlay lives outside `previewSheets`, so dropping it changes nothing the widget can see —
		// it has to be remounted back onto the pristine workbook.
		setFormattingResetKey((current) => current + 1);
	}, []);

	/** The opt-in bundle. Off for the 530, whose fixed government layout is out of scope for formatting. */
	const previewFormatting = useMemo(
		() => (is530 ? undefined : { onStyleOverlayChange: handleStyleOverlayChange, styleOverlay, originalSheets: previewSheets }),
		[is530, handleStyleOverlayChange, styleOverlay, previewSheets],
	);

	// A programmatic width reset re-enters the preview's change callback; the preview swallows that
	// echo when armed, so it can't loop.
	const previewRef = useRef<SpreadsheetPreviewHandle>(null);

	const handleResetColumnWidths = useCallback(() => {
		const currentWidths = printoutSettingsRef.current.printoutColumnWidths;
		if (!currentWidths) return;
		previewRef.current?.suppressNextColumnWidthsChange();
		const resetWidths: Record<string, number> = {};
		for (const key of Object.keys(currentWidths)) {
			resetWidths[key] = DEFAULT_COLUMN_WIDTH;
		}
		updateSetting("printoutColumnWidths", resetWidths);
	}, [updateSetting]);

	const [showConfirmDialog, setShowConfirmDialog] = useState(false);
	const handleSaveAsDefault = useCallback(() => {
		setShowConfirmDialog(true);
	}, []);
	const onConfirmSaveAsDefault = useCallback(async () => {
		setShowConfirmDialog(false);
		clearTimeoutIfNeeded();
		hasChanged.current = false;
		await updateSettings(settingsToPayload, true).catch(() => {});
		closeModal();
	}, [updateSettings, settingsToPayload, closeModal, clearTimeoutIfNeeded]);

	const handleColumnWidthsChange = useCallback(
		(widths: Record<string, number>) => {
			// `reloadPreview: false` — the widths are already applied on screen; rebuilding would
			// discard the drag the user just made.
			updateSetting("printoutColumnWidths", widths, false);
		},
		[updateSetting],
	);

	const isDragging = useRef<boolean>(false);

	const handleResizeStart = useCallback(
		(e: MouseEvent) => {
			e.preventDefault();
			isDragging.current = true;

			const onMouseMove = (moveEvent: globalThis.MouseEvent) => {
				if (!isDragging.current || !containerRef.current) return;
				const containerRect = containerRef.current.getBoundingClientRect();
				const newWidth = moveEvent.clientX - containerRect.left;
				setSettingsWidth(Math.max(MIN_PANEL_WIDTH, Math.min(newWidth, containerRect.width - MIN_PANEL_WIDTH)));
			};

			const onMouseUp = () => {
				isDragging.current = false;
				document.removeEventListener("mousemove", onMouseMove);
				document.removeEventListener("mouseup", onMouseUp);
				// The 530's preview columns are fitted to the panel width, so a resize has to rebuild them.
				// Only on release, not per frame: each rebuild regenerates the workbook.
				if (selectedType === "530") loadPreviewRef.current();
			};

			document.addEventListener("mousemove", onMouseMove);
			document.addEventListener("mouseup", onMouseUp);
		},
		[selectedType],
	);

	return (
		<>
			<Box component={motion.div} sx={{ display: "inline-block" }}>
				<SystemButton
					data-testid="day-schedule-print-trigger"
					buttonVariant="secondary"
					size="small"
					onClick={openModal}
					tooltip={t("download.day.view")}
					sx={{ border: "none", bgcolor: "transparent", "&:disabled": { bgcolor: "transparent" } }}
					leadingIcon={<PrintOutlined sx={{ color: "primary.main" }} />}
				/>
			</Box>
			<Modal
				data-testid="day-schedule-printout-modal"
				open={isModalOpen}
				onClose={closeModal}
				sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
			>
				<Box
					sx={{
						bgcolor: "background.paper",
						borderRadius: 2,
						p: 3,
						width: "95vw",
						maxWidth: 1400,
						height: "85vh",
						outline: "none",
						boxShadow: 24,
						display: "flex",
						flexDirection: "column",
					}}
				>
					<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
						<Box
							sx={{
								display: "flex",
								gap: 1,
								alignItems: "center",
							}}
						>
							<Typography variant="h6" sx={{ fontWeight: 600, color: INK.SECONDARY }}>
								{t("download.day.view")}
							</Typography>
							{showActual && <Chip label={t("download.actual.data.indicator")} color="warning" size="small" />}
						</Box>
						<IconButton data-testid="day-schedule-printout-close-button" onClick={closeModal} size="small">
							<Close />
						</IconButton>
					</Box>

					<Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
						<SystemFlatSwitch options={downloadOptions} activeIndexOrValue={selectedType} onSwitch={(option) => setSelectedType(option.value)} />
					</Box>

					<Box ref={containerRef} sx={{ flex: 1, display: "flex", minHeight: 0 }}>
						<Box sx={{ width: settingsWidth, flexShrink: 0, display: "flex", flexDirection: "column", gap: 1, minHeight: 0 }}>
							{/* Above the settings panel, not inside it: the panel is shared with the summary and
							    spreadsheet modes and with the 530's sibling slot, while this is about the PREVIEW,
							    which is this component's concern. Shown only when there is formatting to lose,
							    because an always-on banner is noise — but via `open` rather than a `&&`: the hint
							    is ~116px, so mounting and unmounting it shoves the settings panel down and snaps
							    it back, and a `&&` would unmount it before any exit could run. */}
							<InlineHint
								open={hasFormatting}
								dataTestId="printout-formatting-notice"
								tone="info"
								actionPlacement="below"
								action={
									<SystemButton data-testid="printout-formatting-clear" buttonVariant="borderless" size="small" onClick={clearFormatting}>
										{t("printout.formatting.clear")}
									</SystemButton>
								}
							>
								{t("printout.formatting.notice")}
							</InlineHint>
							{/* The panels size themselves to 100% of their parent, so the hint above has to take its
							    space from a sibling rather than from them. */}
							<Box sx={{ flex: 1, minHeight: 0 }}>
								{is530 ? (
									<Form530SettingsPanel
										form530Settings={form530Settings}
										updateForm530Setting={updateForm530Setting}
										printoutSettings={printoutSettings}
										updateSetting={updateSetting}
									/>
								) : (
									<PrintoutSettingsPanel
										printoutSettings={printoutSettings}
										updateSetting={updateSetting}
										disabled={selectedType !== "detailed"}
										enabledSections={selectedType === "summary" ? SUMMARY_ENABLED_SECTIONS : undefined}
										availableGroupingTypes={selectedType === "summary" ? SUMMARY_GROUPING_TYPES : undefined}
										isDetailed={selectedType === "detailed"}
										// Detailed only: the AI configurator writes the detailed layout settings, which the
										// summary and spreadsheet printouts don't use.
										onApplySettings={selectedType === "detailed" ? applySettings : undefined}
									/>
								)}
							</Box>
						</Box>

						{/* Resize Handle */}
						<Box
							onMouseDown={handleResizeStart}
							sx={(theme) => ({
								width: 8,
								flexShrink: 0,
								cursor: "col-resize",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								"&:hover > div, &:active > div": { bgcolor: theme.palette.primary.dark },
							})}
						>
							<Box sx={{ width: 3, height: 40, borderRadius: 2, bgcolor: NEUTRAL[400], transition: "background-color 0.2s" }} />
						</Box>

						{/* Preview Panel */}
						<SpreadsheetPreview
							ref={previewRef}
							dataTestId="day-schedule-preview"
							sheets={previewSheets}
							isLoading={isLoadingPreview}
							// The 530's column widths come from the form's own fixed layout, so a drag on its preview
							// must not be written back as a day-view width override.
							onColumnWidthsChange={is530 ? undefined : handleColumnWidthsChange}
							columnWidths={printoutSettings.printoutColumnWidths}
							formatting={previewFormatting}
							remountKey={formattingResetKey}
							sx={{ flex: 1 }}
						/>
					</Box>

					<Box sx={{ mt: 2, display: "flex", justifyContent: is530 ? "flex-end" : "space-between", alignItems: "center" }}>
						{is530 ? (
							<DownloadFormatButton
								dataTestIdPrefix="day-schedule-530"
								formats={["xlsx", "pdf"]}
								onDownload={(format) => void run530Download(format)}
								isDownloading={isDownloading530}
								// No preview sheets means no workbook to export — the 530 PDF is rendered from them.
								disabled={isLoadingPreview || isDownloading530 || previewSheets.length === 0}
								onPrint={() => void run530Print()}
								isPrinting={isPrinting}
							/>
						) : (
							<>
								<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
									{printoutSettings.printoutColumnWidths && (
										<SystemButton buttonVariant="secondary" size="small" onClick={handleResetColumnWidths} leadingIcon={<RestartAlt />}>
											{t("printout.reset.column.widths")}
										</SystemButton>
									)}
									{saveAsDefaultEnabled && (
										<SystemButton
											data-testid="printout-save-as-default"
											buttonVariant="secondary"
											size="small"
											onClick={handleSaveAsDefault}
											loading={isUpdatingSettings}
											leadingIcon={<SaveOutlined />}
										>
											{t("settings.save.as.default")}
										</SystemButton>
									)}
								</Box>
								<DownloadFormatButton
									dataTestIdPrefix="day-schedule"
									formats={["xlsx", "pdf"]}
									onDownload={(format) => runDownload(format === "xlsx" ? "excel" : "pdf")}
									isDownloading={disableDownload}
									disabled={disableDownload}
									onPrint={() => void runPrint()}
									isPrinting={isPrinting}
								/>
							</>
						)}
					</Box>
				</Box>
			</Modal>
			{saveAsDefaultEnabled && <SaveAsDefaultDialog open={showConfirmDialog} onClose={() => setShowConfirmDialog(false)} onConfirm={onConfirmSaveAsDefault} />}
		</>
	);
};
